import { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import { Download, Plus, Trash2, Upload } from "lucide-react";
import Button from "../../../components/Buttons/Button";
import IconButton from "../../../components/Buttons/IconButton";
import TextField from "../../../components/Form/TextField";
import TableContainer from "../../../components/Tables/TableContainer";
import TableCell from "../../../pages/BillingAndPayments/components/TableCell";
import { useTranslationApp } from "../../../lib/i18next";
import { TASK_ALERT_ERROR_MESSAGE_BG } from "../../../utils/colors";
import {
  TEMPLATES_PRODUCE_ADD_ROW,
  TEMPLATES_PRODUCE_DELETE_ROW,
  TEMPLATES_PRODUCE_DOWNLOAD_FORMAT,
  TEMPLATES_PRODUCE_ROWS,
  TEMPLATES_PRODUCE_ROWS_HINT,
  TEMPLATES_PRODUCE_UPLOAD_CSV,
} from "../../../i18n/keysTranslations";
import ProduceImageCell from "./ProduceImageCell";

const optionalRules = { required: false };

const cellSx = {
  whiteSpace: "normal",
  overflow: "visible",
  textOverflow: "unset",
};

const ProduceRowsTable = ({
  control,
  columns = [],
  fields = [],
  rows = [],
  invalidRowIndexes = [],
  pathStorage = "",
  disabled = false,
  onAddRow = () => {},
  onRemoveRow = () => {},
  onChangeImage = () => {},
  onUploadCsv = () => {},
  onDownloadFormat = () => {},
  rowRefs,
}) => {
  const { t } = useTranslationApp();
  const [editingCell, setEditingCell] = useState(null);
  const invalidSet = new Set(invalidRowIndexes);

  const onPickCsv = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) onUploadCsv({ file });
  };

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "#fff",
        border: "1px solid #E4E4E7",
        borderRadius: "14px",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: "1.125rem" }}>
            {t(TEMPLATES_PRODUCE_ROWS)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(TEMPLATES_PRODUCE_ROWS_HINT)}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={disabled ? undefined : onDownloadFormat}
            disabled={disabled}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              p: 0,
              border: "none",
              background: "transparent",
              color: "text.secondary",
              fontFamily: "inherit",
              fontSize: "0.875rem",
              lineHeight: 1.4,
              whiteSpace: "nowrap",
              cursor: disabled ? "default" : "pointer",
              opacity: disabled ? 0.5 : 1,
              "&:hover": disabled ? undefined : { color: "text.primary" },
            }}
          >
            <Download size={16} />
            <Box
              component="span"
              sx={{ textDecoration: "underline", textUnderlineOffset: "3px" }}
            >
              {t(TEMPLATES_PRODUCE_DOWNLOAD_FORMAT)}
            </Box>
          </Box>
          <Button
            color="secondary"
            type="button"
            startIcon={<Plus size={16} />}
            onClick={onAddRow}
            disabled={disabled}
            sx={{ minWidth: 120, height: 36 }}
          >
            {t(TEMPLATES_PRODUCE_ADD_ROW)}
          </Button>
          <Button
            color="secondary"
            component="label"
            startIcon={<Upload size={16} />}
            disabled={disabled}
            sx={{ minWidth: 120, height: 36 }}
          >
            {t(TEMPLATES_PRODUCE_UPLOAD_CSV)}
            <input
              type="file"
              accept=".csv"
              hidden
              disabled={disabled}
              onChange={onPickCsv}
            />
          </Button>
        </Box>
      </Box>
      <TableContainer
        keys={fields}
        hasPagination={false}
        showEmptyText={false}
        elevation={0}
        rounded={false}
        sx={{ borderRadius: "0px !important", boxShadow: "none" }}
        sxContainer={{
          boxShadow: "none",
          borderRadius: "0px !important",
          minWidth: 0,
          maxWidth: "100%",
          maxHeight: 600,
          overflow: "auto",
        }}
        sx={{
          borderRadius: "0px !important",
          boxShadow: "none",
          maxHeight: 600,
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell
              isHeader
              sx={{ minWidth: 48, width: 48, pl: "16px !important" }}
            >
              #
            </TableCell>
            {columns.map((column) => (
              <TableCell key={column.key} isHeader>
                {column.label}
              </TableCell>
            ))}
            <TableCell isHeader sx={{ minWidth: 56, width: 56 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.map((field, index) => {
            const isLastRow = index === fields.length - 1;
            const isInvalid = invalidSet.has(index);
            const rowColor = isInvalid ? TASK_ALERT_ERROR_MESSAGE_BG : "";
            const rowValues = rows[index] || {};
            return (
              <TableRow
                key={field.id}
                ref={(node) => {
                  if (!rowRefs?.current) return;
                  rowRefs.current[index] = node;
                }}
                data-produce-row={index}
              >
                <TableCell
                  indexRow={index}
                  isLastRow={isLastRow}
                  customColor={rowColor}
                  sx={{ minWidth: 48, pl: "16px !important" }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {index + 1}
                  </Typography>
                </TableCell>
                {columns.map((column) => {
                  const isImage = column.type === "image";
                  const isEditing =
                    editingCell?.index === index &&
                    editingCell?.key === column.key;
                  const cellName = `rows.${index}.${column.key}`;
                  return (
                    <TableCell
                      key={`${field.id}-${column.key}`}
                      indexRow={index}
                      isLastRow={isLastRow}
                      customColor={rowColor}
                      sx={
                        isImage
                          ? { ...cellSx, minWidth: 160 }
                          : { minWidth: 160 }
                      }
                      onClick={() => {
                        if (disabled || isImage) return;
                        setEditingCell({ index, key: column.key });
                      }}
                    >
                      {isImage ? (
                        <ProduceImageCell
                          url={rowValues[column.key] || ""}
                          inputId={`produce-image-${index}-${column.key}`}
                          pathStorage={pathStorage}
                          disabled={disabled}
                          onChange={(nextUrl) =>
                            onChangeImage({ index, key: column.key, nextUrl })
                          }
                        />
                      ) : isEditing ? (
                        <TextField
                          control={control}
                          name={cellName}
                          rules={optionalRules}
                          variant="filled"
                          size="small"
                          fullWidth
                          autoFocus
                          actionsBlur={() => setEditingCell(null)}
                          onKeyDown={(event) => {
                            if (event.key !== "Enter") return;
                            event.preventDefault();
                            event.target.blur();
                          }}
                          sx={{ minWidth: 140 }}
                        />
                      ) : (
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            color: rowValues[column.key]
                              ? "text.primary"
                              : "text.disabled",
                          }}
                        >
                          {rowValues[column.key] || "—"}
                        </Typography>
                      )}
                    </TableCell>
                  );
                })}
                <TableCell
                  indexRow={index}
                  isLastRow={isLastRow}
                  customColor={rowColor}
                  sx={{ minWidth: 56 }}
                >
                  <IconButton
                    tooltipText={TEMPLATES_PRODUCE_DELETE_ROW}
                    disabled={disabled}
                    onClick={(event) => {
                      event.stopPropagation();
                      onRemoveRow({ index });
                    }}
                    sx={{
                      width: 32,
                      height: 32,
                      color: "text.primary",
                      "&:hover": { color: "text.primary" },
                    }}
                  >
                    <Trash2 size={16} />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </TableContainer>
    </Box>
  );
};

export default ProduceRowsTable;
